/**
 * The navigation manifest — the single source of truth for what the side nav
 * contains (SAL-2912).
 *
 * Before this file the same routes were written down twice: once as JSX-adjacent
 * arrays inside the header component, and again inside
 * `lib/agent-tools/navigation-pages.js` so ⌘K could find them. Keeping the two
 * in step was a rule in CLAUDE.md and a parity test — which is to say, it was
 * someone's memory. Three routes had already fallen out of the search index.
 * The per-area sidebars (Results, Admin, Reports) were a third copy and had
 * drifted too, so they now read these menus as well.
 *
 * Now the menus are declared here, as data, and everything else derives:
 *
 *   • `components/nav/SideNav.jsx` renders them: one icon per menu on the
 *     rail, the selected menu's items in the panel, every menu in the mobile
 *     drawer.
 *   • `lib/agent-tools/navigation-pages.js` flattens them into the ⌘K index.
 *   • `lib/navigation/nav-search-open.js` maps a menu label to its index
 *     section from the same entries.
 *   • `components/nav/PageHeading.jsx` prints a page's H1 and the line under
 *     it from its entry, so a page cannot be headed one thing and indexed as
 *     another.
 *   • `scripts/check-nav-routes.mjs` (in `pnpm lint`) fails on an href no
 *     page file under `app/` serves, and on a page that is neither listed here
 *     nor allowlisted there.
 *
 * Adding a nav link means adding an entry here. It should not mean editing a
 * component, and it must not mean editing the search index.
 *
 * Like `lib/plugins/registry.js`, this file is deliberately dependency-free:
 * plain data, icons by name, conditions by name. That is what lets it be
 * imported from client components, server code, route guards and unit tests
 * alike. Anything that needs React, Prisma or the session belongs in
 * `lib/navigation/nav-items.js`, which turns this data into rendered items.
 *
 * @typedef {object} NavManifestItem
 * @property {string} [href]      Destination. Omit for a group — a heading whose
 *   children are the links. May contain `{workspaceId}`, substituted per studio.
 * @property {string} label       Menu wording. Studio module renaming may
 *   override it (see `moduleLabels` below).
 * @property {string} [heading]   The page's own H1, when it is not `label`: the
 *   menu says "Modules", the page says "Modules manager".
 *   `components/nav/PageHeading.jsx` renders it (the label otherwise) and ⌘K
 *   matches it, so the two cannot drift. One entry per href declares it, and
 *   an href listed under different labels must (navigationManifest.test.js).
 * @property {string} [description]  The line beneath the page's H1, rendered
 *   and indexed the same way.
 * @property {string} [iconName]  Key into components/Sidebar/IconMapping.
 *   One label has one icon in every menu (navigationManifest.test.js), and a
 *   page that shows the concept reads it through `getNavIcon(href)`.
 * @property {string} [shortcut]  One capital letter: Option/Alt + it goes
 *   straight to this page (lib/keyboard.js). Unique across the manifest;
 *   `navigationManifest.test.js` holds that.
 * @property {boolean} [external] Opens off-site; rendered as a plain anchor and
 *   never indexed for search.
 * @property {NavRequirement} [requires]  When this entry is visible. Absent
 *   means always. A group is additionally hidden when nothing under it survives,
 *   so a heading never renders above an empty list.
 * @property {NavManifestItem[]} [subItems]
 *
 * @typedef {string | string[] | {all?: NavRequirement[], any?: NavRequirement[]}} NavRequirement
 *   A condition name from `NAV_CONDITIONS` in lib/navigation/nav-items.js, an
 *   array of them (all must hold), or an explicit `all`/`any` combination —
 *   given both, both have to hold. A name `NAV_CONDITIONS` does not define
 *   hides the entry, so a typo fails closed rather than opening a gated link.
 *
 * @typedef {object} NavManifestMenu
 * @property {string} key         Stable id, used by code that needs one menu.
 * @property {string} menuLabel   The menu's name — on its rail button and at
 *   the top of its panel.
 * @property {string} iconName    Its rail icon; a key into
 *   components/Sidebar/IconMapping.
 * @property {string} section     What the ⌘K index calls this menu. Usually the
 *   same word; "My Results" is indexed as "Results" and "Team Reports" as
 *   "Reports", which is the mapping `nav-search-open.js` needs.
 * @property {string} [subtitle]  Small print under the menu name.
 * @property {NavRequirement} [requires]  When the whole menu is offered.
 * @property {string[]} activePrefixes  Route prefixes that mark this menu as the
 *   current one. Menus may overlap — the most specific match wins, so only one
 *   menu is ever active (`resolveActiveNavKey` in lib/navigation/nav-items.js).
 * @property {string} [moduleLabels]  Which relabelling map applies to items in
 *   this menu, for studios that renamed a module ("dashboard" | "report" |
 *   "admin" | "playbook" | "practice"). See lib/modules/display-names.js.
 * @property {boolean} [overrideExempt]  Skip the studio's `menuOverride`
 *   allow-list. Plugin filtering still applies.
 * @property {{prompt: string, label: string, href: string, requires?: NavRequirement}} [crossLink]
 *   Footer link to the sibling menu people confuse this one with.
 * @property {NavManifestItem[]} items
 */

import { ADMIN_ITEMS } from "@/lib/navigation/manifest/admin";
import { EXECUTION_ITEMS } from "@/lib/navigation/manifest/execution";
import { LEARN_ITEMS } from "@/lib/navigation/manifest/learn";
import { PRACTICE_ITEMS } from "@/lib/navigation/manifest/practice";
import { REPORT_ITEMS } from "@/lib/navigation/manifest/reports";
import { RESULTS_ITEMS } from "@/lib/navigation/manifest/results";

/** @type {NavManifestMenu[]} */
export const NAV_MANIFEST = [
  {
    key: "learn",
    menuLabel: "Learn",
    section: "Learn",
    iconName: "IoBookOutline",
    activePrefixes: [
      "/playbook",
      "/dashboard/learning-paths",
      "/dashboard/tests",
      "/training",
      "/questions",
    ],
    moduleLabels: "playbook",
    items: LEARN_ITEMS,
  },
  {
    key: "practice",
    menuLabel: "Practice",
    section: "Practice",
    iconName: "LiaTheaterMasksSolid",
    activePrefixes: [
      "/simulator",
      "/dashboard/assignments",
      "/dashboard/role-plays",
      "/stories",
    ],
    moduleLabels: "practice",
    items: PRACTICE_ITEMS,
  },
  {
    key: "execution",
    menuLabel: "Execution",
    section: "Execution",
    iconName: "IoFlashOutline",
    activePrefixes: [
      "/dem-cog/reports",
      "/dashboard/transcript-results",
      // The analyzer is a step on the way to Conversations, so it keeps
      // Execution lit rather than My Results' "/dashboard".
      "/dashboard/conversation-analyzer",
      "/workspaces/scripts",
      "/guide",
    ],
    moduleLabels: "dashboard",
    items: EXECUTION_ITEMS,
  },
  {
    key: "results",
    menuLabel: "My Results",
    section: "Results",
    iconName: "IoPersonOutline",
    subtitle: "Your personal scores and progress",
    activePrefixes: ["/dashboard", "/leaderboard"],
    moduleLabels: "dashboard",
    crossLink: {
      prompt: "Looking for your team's results?",
      label: "Team",
      href: "/workspaces/reports",
      requires: "adminOrSupervisorOrLeader",
    },
    items: RESULTS_ITEMS,
  },
  {
    key: "reports",
    menuLabel: "Team",
    section: "Reports",
    iconName: "IoBarChartOutline",
    subtitle: "The people you're responsible for",
    requires: "adminOrSupervisorOrLeader",
    // Studio Usage is an admin page by address but a team report by purpose.
    activePrefixes: ["/workspaces/reports", "/workspaces/admin/analytics"],
    moduleLabels: "report",
    crossLink: {
      prompt: "Looking for your own results?",
      label: "My Results",
      href: "/dashboard",
    },
    items: REPORT_ITEMS,
  },
  {
    key: "admin",
    menuLabel: "Admin",
    section: "Admin",
    iconName: "HiOutlineCog8Tooth",
    requires: "adminOrSupervisorOrLeader",
    // Every /workspaces route except the reports ones, which belong to the menu
    // above. Overlap is fine: `resolveActiveNavKey` gives a route to the menu
    // with the most specific matching prefix, so "/workspaces/reports/..."
    // highlights Team Reports rather than both menus at once.
    activePrefixes: ["/supervisor", "/workspaces"],
    moduleLabels: "admin",
    // Admins must be able to reach the settings that produce a menuOverride,
    // or a stripped studio cannot be reconfigured from inside the app.
    overrideExempt: true,
    items: ADMIN_ITEMS,
  },
  {
    // Every conversation with Sabine. The panel lists every one, newest
    // first, below this link (components/nav/useChatsMenu.js); each opens where
    // the chat happened, with her pane open, so it carries on. Last on the
    // rail, right below Admin.
    key: "chats",
    menuLabel: "Chats",
    section: "Chats",
    iconName: "SabineFace",
    subtitle: "Your conversations with Sabine",
    requires: "workspace",
    activePrefixes: ["/chats"],
    items: [
      {
        href: "/chats",
        label: "All chats",
        heading: "Chats with Sabine",
        description:
          "Every conversation you've had with Sabine. Open one to carry on where you left off.",
        iconName: "IoEllipsisHorizontal",
      },
    ],
  },
];

/**
 * Training mode: the rail everyone else has, with fewer buttons. Home opens
 * the rep's training (`TRAINING_MODE_HOME`, which is Learn › My Training for
 * everyone else), then My Results and Chats, so a rep who leaves training
 * mode finds every name where it was. The Playbook is left off: reps found
 * it confusing in training mode (SAL-3378). `buildNavSections` assembles the
 * rail from these; nothing else may be reached from it.
 */
export const TRAINING_MODE_HOME = "/training";

/** The manifest menus a training-mode rep gets. */
export const TRAINING_MODE_MENU_KEYS = ["results", "chats"];

/**
 * Destinations no menu lists — reachable from the avatar menu or a footer
 * link — declared here so ⌘K finds them and PageHeading names them like any
 * other page. `section` is the ⌘K section each one is listed under.
 *
 * `requiresWorkspace` mirrors the gate on the control that links to them, the
 * way menu entries carry `requires: "workspace"`. It does not currently decide
 * anything: `searchNavigationPages` needs a studio to resolve labels and module
 * gating at all, so it returns nothing before the flag is read. It records what
 * the page needs, so the answer is already right if that condition widens.
 *
 * @type {(NavManifestItem & {section: string, requiresWorkspace: boolean})[]}
 */
export const OFF_MENU_PAGES = [
  {
    href: "/settings",
    label: "Account Settings",
    section: "Account",
    requiresWorkspace: true,
  },
  {
    href: "/help",
    label: "Help Center",
    heading: "How can we help?",
    section: "Support",
    requiresWorkspace: false,
  },
  {
    href: "/help/member",
    label: "Member Help Center",
    section: "Support",
    requiresWorkspace: false,
  },
  {
    href: "/help/member/practice-sessions",
    label: "Practice Sessions Guide",
    section: "Support",
    requiresWorkspace: false,
  },
  {
    href: "/help/admin/ai-assistants",
    label: "Salesably in Claude and ChatGPT",
    section: "Support",
    requiresWorkspace: false,
  },
  {
    // The SuperUser console has its own menu; this entry is what PageHeading
    // prints and what ⌘K finds, for superUsers only (SAL-3382).
    href: "/admin/superUser/assistant-activity",
    label: "Claude and ChatGPT activity",
    description:
      "Every tool call a connected assistant made, by session, with what it asked for and what came back.",
    section: "SuperUser",
    requiresWorkspace: false,
    requires: "superUser",
  },
  {
    // Off the My Results menu since 2 November 2026; kept for search.
    href: "/dashboard/persona-chats",
    label: "Persona Chats",
    heading: "Persona chats",
    description:
      "Open the transcripts and reports from your persona chat sessions. Only the most recent chat per persona is kept.",
    section: "Results",
    requiresWorkspace: true,
  },
  {
    href: "/support/connection-check",
    label: "Connection Check",
    description:
      "Tests whether voice practice can connect from this device and gives you a report to send to support.",
    section: "Support",
    requiresWorkspace: true,
  },
];

const BY_KEY = new Map(NAV_MANIFEST.map((menu) => [menu.key, menu]));

/** @returns {NavManifestMenu | null} */
export function getNavMenu(key) {
  return BY_KEY.get(key) || null;
}

/** Header menu label → the section name the ⌘K index uses for it. */
export const NAV_MENU_LABEL_SECTIONS = Object.fromEntries(
  NAV_MANIFEST.map((menu) => [menu.menuLabel, menu.section]),
);
